'use client';

import React, { useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Row, Button, FormControl } from '@stape-io/promo-shared-content/components';
import clsx from 'clsx';
import { useClickAway } from 'react-use';

import { useClientTranslation } from '@/lib/i18n/client';
import { useSearchResults } from '@/lib/hooks/useSearchResults';
import { I18nTrans } from '@/lib/components/I18nTrans';
import { CircleAnimation } from '@/lib/components/CircleAnimation';
import { SearchDropdown } from '@/shared/SearchDropdown';
import { SearchCategory, SearchHit } from '@/lib/models/Search';
import { SearchDropdownWrapper } from '@/shared/SearchDropdown/components/SearchDropdownWrapper';
import { SearchDropdownTopSearches } from '@/shared/SearchDropdown/components/SearchDropdownTopSearches';
import { SearchDropdownHeader } from '@/shared/SearchDropdown/components/SearchDropdownHeader';
import { getCategories } from '@/lib/utils/getCategories';

import styles from './HeroSectionClient.module.scss';

interface HeroSectionProps {
  topSearches: SearchHit[] | null;
  locale: string;
}

export function HeroSectionClient({ topSearches, locale }: HeroSectionProps) {
  const router = useRouter();
  const { t } = useClientTranslation('hero');

  const { resultsByCategory, loading, setQuery, query } = useSearchResults(locale);
  const categories = resultsByCategory && getCategories(resultsByCategory);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<SearchCategory>('all');

  const [isOpen, setIsOpen] = useState(false);

  useClickAway(searchContainerRef, () => setIsOpen(false));

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    setIsOpen(newQuery.trim().length > 0 || !!topSearches?.length);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!query.trim()) return;
    router.push(`/helpdesk/search?q=${encodeURIComponent(query)}`);
    setIsOpen(false);
  };

  return (
    <section className={styles.heroSection}>
      <Row className="text">
        <h1 className={clsx(styles.heroTitle, 'h1')}>
          <I18nTrans components={{ circleAnimation: <CircleAnimation /> }} ns="hero" i18nKey="hero.title" />
        </h1>
        <p className={clsx(styles.heroDescription, 'text-lg')}>{t('hero.searchAllText')}</p>

        <div ref={searchContainerRef} className={clsx(styles.searchContainer)}>
          <form action="/helpdesk/search" onSubmit={handleSubmit} className={styles.searchForm}>
            <FormControl className={clsx(styles.searchFormControl, 'text-lg')}>
              <input
                type="text"
                name="q"
                placeholder={t('hero.searchAllPlaceholderMobile')}
                value={query}
                onChange={handleInputChange}
                onFocus={() => setIsOpen(true)}
              />
            </FormControl>
            <Button type="submit" className={clsx(styles.searchButton, 'text-lg text-bold')} loading={loading}>
              {loading ? t('hero.searchLoading') : t('hero.searchButton')}
            </Button>
          </form>
          {isOpen && (
            <>
              {query.trim().length ? (
                <SearchDropdown query={query} results={resultsByCategory?.[activeTab] || null} close={setIsOpen} className="hero">
                  <SearchDropdownHeader activeTab={activeTab} setActiveTab={setActiveTab} categories={categories} />
                </SearchDropdown>
              ) : (
                <SearchDropdownWrapper className="hero">
                  <SearchDropdownTopSearches topSearches={topSearches} />
                </SearchDropdownWrapper>
              )}
            </>
          )}
        </div>
      </Row>
    </section>
  );
}
